// Pulls prices for every asset directly from the user's own data sources (design.md §3):
// US equities via Finnhub (real-time) or Massive (15-min delayed), else TMX Money (no key, delayed); Canadian
// equities via Twelve Data, Finnhub's paid plans, or TMX Money as the fallback; Canadian mutual funds via TMX Money
// by FundSERV code; crypto via Coinbase; FX via Frankfurter. So with no key at all, everything is still priced.
import { t } from '@/i18n';
import { finnhubKey, massiveKey, settingsStore, twelvedataKey } from '@/settings/settingsStore';
import { appStore } from '@/state/appStore';
import { quoteKey, type Asset, type Quote, type Snapshot } from '@/types';
import { coinbaseQuote } from './coinbase';
import { describeMarketError, MarketError } from './errors';
import { finnhubQuote } from './finnhub';
import { fetchFxRates } from './frankfurter';
import { isUsMarketOpen, todayNY, usSessionPhase } from './marketHours';
import { massiveSnapshots } from './massive';
import { tmxQuotes, tmxSymbol, type TmxRequest } from './tmx';
import { twelvedataQuote } from './twelvedata';
import { computeTotals } from './value';

const POLL_MS = 60_000;
const FX_TTL_MS = 6 * 60 * 60 * 1000;
const FUND_TTL_MS = 4 * 60 * 60 * 1000; // NAVs are published once an evening

let timer: ReturnType<typeof setInterval> | undefined;
let inFlight: Promise<void> | null = null;
let lastEquityRefresh = 0;
let lastEquityPhase: 'pre' | 'open' | 'post' | null = null;
let lastFundRefresh = 0;

// A source that keeps failing would otherwise toast every tick; report each failure once, and again only after it recovered.
const failing = new Map<string, string>();
function reportFailure(scope: string, message: string) {
  if (failing.get(scope) === message) return;
  failing.set(scope, message);
  appStore.notify(message);
}
function reportRecovered(scope: string) {
  failing.delete(scope);
}

export function startQuotePolling() {
  stopQuotePolling();
  appStore.loadCachedFx();
  const tick = () => {
    if (document.visibilityState !== 'visible') return;
    void refreshAll();
  };
  tick();
  timer = setInterval(tick, POLL_MS);
  document.addEventListener('visibilitychange', tick);
}

export function stopQuotePolling() {
  if (timer) clearInterval(timer);
  timer = undefined;
}

/** Refreshes FX, crypto and (when the market is open or the phase changed) equities. Never throws; errors become stale flags + a notice. */
export function refreshAll(force = false): Promise<void> {
  if (inFlight) return inFlight;
  inFlight = (async () => {
    appStore.setRefreshing(true);
    try {
      const { assets } = appStore.get();
      await Promise.all([refreshFx(assets, force), refreshCrypto(assets), refreshEquities(assets, force), refreshFunds(assets, force)]);
      await maybeRecordSnapshot();
    } finally {
      appStore.setRefreshing(false);
      inFlight = null;
    }
  })();
  return inFlight;
}

async function refreshFx(assets: Asset[], force: boolean) {
  const fx = appStore.get().fx;
  if (!force && fx && Date.now() - fx.fetchedAt < FX_TTL_MS) return;
  const extra = assets.filter((a) => a.assetClass === 'cash').map((a) => a.currency);
  try {
    appStore.setFx(await fetchFxRates(extra));
  } catch (e) {
    if (!fx) appStore.notify(t('notice.fxFailed', { error: describeMarketError(e) }));
  }
}

async function refreshCrypto(assets: Asset[]) {
  const symbols = Array.from(new Set(assets.filter((a) => a.assetClass === 'crypto').map((a) => a.symbol.toUpperCase())));
  if (symbols.length === 0) return;
  const results = await Promise.allSettled(symbols.map((s) => coinbaseQuote(s)));
  const ok: Quote[] = [];
  const failed: string[] = [];
  results.forEach((r, i) => (r.status === 'fulfilled' ? ok.push(r.value) : failed.push(`cx:${symbols[i]}`)));
  await appStore.setQuotes(ok);
  if (failed.length) appStore.markStale(failed);
}

// A free Finnhub key answers 403 for Canadian symbols; remember that until the keys change instead of retrying every minute.
let caDenied = false;
settingsStore.subscribe(() => {
  caDenied = false;
});

async function refreshEquities(assets: Asset[], force: boolean) {
  const equities = assets.filter((a) => a.assetClass === 'stock' || a.assetClass === 'etf');
  if (equities.length === 0) return;

  // Outside the session prices do not move (Toronto keeps New York hours): fetch once when the phase changes, then rest.
  const phase = usSessionPhase();
  const open = isUsMarketOpen();
  if (!force && !open && lastEquityPhase === phase && Date.now() - lastEquityRefresh < 6 * 60 * 60 * 1000) return;

  await Promise.all([refreshUsEquities(equities.filter((a) => a.market !== 'CA'), open), refreshCanadianEquities(equities.filter((a) => a.market === 'CA'), open)]);
  lastEquityRefresh = Date.now();
  lastEquityPhase = phase;
}

/** Fulfilled quotes; when nothing succeeded, the first failure is the reason. */
async function settled(tasks: Promise<Quote>[]): Promise<Quote[]> {
  const results = await Promise.allSettled(tasks);
  const quotes = results.flatMap((r) => (r.status === 'fulfilled' ? [r.value] : []));
  const firstFailure = results.find((r) => r.status === 'rejected') as PromiseRejectedResult | undefined;
  if (quotes.length === 0 && firstFailure) throw firstFailure.reason;
  return quotes;
}

/** Canadian listings: Twelve Data, then a paid Finnhub key, then TMX Money for everyone else. */
async function canadianQuotes(listings: { symbol: string; mic?: string }[]): Promise<Quote[]> {
  const settings = settingsStore.get();
  const td = twelvedataKey(settings);
  const fh = finnhubKey(settings);
  if (td) return settled(listings.map((l) => twelvedataQuote(l.symbol, l.mic, td)));
  if (fh && !caDenied) {
    try {
      return await settled(listings.map((l) => finnhubQuote(l.symbol, fh, undefined, { market: 'CA', mic: l.mic })));
    } catch (e) {
      if (!(e instanceof MarketError && e.code === 'invalid_key')) throw e;
      caDenied = true;
    }
  }
  return (await tmxQuotes(listings.map((l) => ({ key: `eq:CA:${l.symbol}`, symbol: tmxSymbol(l), kind: 'equity' as const })))).quotes;
}

async function refreshCanadianEquities(assets: Asset[], open: boolean) {
  if (assets.length === 0) return;
  const listings = Array.from(new Map(assets.map((a) => [a.symbol.toUpperCase(), { symbol: a.symbol.toUpperCase(), mic: a.mic }])).values());
  const keys = listings.map((l) => `eq:CA:${l.symbol}`);
  try {
    let quotes = await canadianQuotes(listings);
    if (!open) quotes = quotes.map((q) => ({ ...q, freshness: 'close' as const }));
    await appStore.setQuotes(quotes);
    const got = new Set(quotes.map((q) => q.key));
    appStore.markStale(keys.filter((k) => !got.has(k)));
    reportRecovered('ca');
  } catch (e) {
    appStore.markStale(keys);
    reportFailure('ca', t('notice.quotesFailed', { error: describeMarketError(e) }));
  }
}

/** FundSERV-coded funds (Canadian) get their NAV from TMX Money; other funds keep the NAV entered by hand. */
function tmxFundRequests(assets: Asset[]): TmxRequest[] {
  return Array.from(new Map(assets.filter((a) => a.assetClass === 'fund' && a.market === 'CA').map((a) => [quoteKey(a), { key: quoteKey(a), symbol: a.symbol.toUpperCase(), kind: 'fund' as const }])).values());
}

/** Refreshes fund NAVs; returns the keys TMX could price. Exported for the list page's "update NAVs" button. */
export async function refreshFunds(assets: Asset[], force: boolean): Promise<Set<string>> {
  const requests = tmxFundRequests(assets);
  if (requests.length === 0) return new Set();
  if (!force && Date.now() - lastFundRefresh < FUND_TTL_MS) return new Set(requests.map((r) => r.key).filter((k) => appStore.get().quotes[k]?.source === 'tmx'));
  const keys = requests.map((r) => r.key);
  try {
    const { quotes } = await tmxQuotes(requests);
    await appStore.setQuotes(quotes);
    const got = new Set(quotes.map((q) => q.key));
    appStore.markStale(keys.filter((k) => !got.has(k)));
    lastFundRefresh = Date.now();
    reportRecovered('fund');
    return got;
  } catch (e) {
    appStore.markStale(keys);
    reportFailure('fund', t('notice.quotesFailed', { error: describeMarketError(e) }));
    return new Set();
  }
}

async function refreshUsEquities(assets: Asset[], open: boolean) {
  const symbols = Array.from(new Set(assets.map((a) => a.symbol.toUpperCase())));
  if (symbols.length === 0) return;
  const settings = settingsStore.get();
  const fh = finnhubKey(settings);
  const mv = massiveKey(settings);
  const keys = symbols.map((s) => `eq:${s}`);

  try {
    let quotes: Quote[];
    if (!fh && !mv) {
      quotes = (await tmxQuotes(symbols.map((s) => ({ key: `eq:${s}`, symbol: tmxSymbol({ symbol: s, market: 'US' }), kind: 'equity' as const })))).quotes;
    } else if (fh) {
      const results = await Promise.allSettled(symbols.map((s) => finnhubQuote(s, fh)));
      quotes = results.flatMap((r) => (r.status === 'fulfilled' ? [r.value] : []));
      const firstFailure = results.find((r) => r.status === 'rejected') as PromiseRejectedResult | undefined;
      if (firstFailure && quotes.length === 0) throw firstFailure.reason;
    } else {
      quotes = await massiveSnapshots(symbols, mv!);
    }
    if (!open) quotes = quotes.map((q) => ({ ...q, freshness: 'close' as const }));
    await appStore.setQuotes(quotes);
    const got = new Set(quotes.map((q) => q.key));
    appStore.markStale(keys.filter((k) => !got.has(k)));
    reportRecovered('us');
  } catch (e) {
    appStore.markStale(keys);
    reportFailure('us', t('notice.quotesFailed', { error: describeMarketError(e) }));
  }
}

/** One valuation snapshot per New York day, taken on the first successful refresh of that day. */
async function maybeRecordSnapshot() {
  const { assets, quotes, fx, snapshots } = appStore.get();
  if (assets.length === 0) return;
  const date = todayNY();
  if (snapshots.some((s) => s.date === date)) return;
  const totals = computeTotals(assets, quotes, fx);
  if (totals.missing > 0) return; // wait until every position is priced
  const snapshot: Snapshot = { date, totalUsd: totals.totalUsd, byPortfolio: totals.byPortfolio, byClass: totals.byClass };
  await appStore.recordSnapshot(snapshot);
}

/** Immediately price one newly added asset so it does not sit at "—" until the next tick. */
export async function primeAsset(asset: Asset) {
  const settings = settingsStore.get();
  try {
    if (asset.assetClass === 'crypto') await appStore.setQuotes([await coinbaseQuote(asset.symbol)]);
    else if (asset.assetClass === 'stock' || asset.assetClass === 'etf') {
      const fh = finnhubKey(settings);
      const mv = massiveKey(settings);
      if (asset.market === 'CA') await appStore.setQuotes(await canadianQuotes([{ symbol: asset.symbol.toUpperCase(), mic: asset.mic }]));
      else if (fh) await appStore.setQuotes([await finnhubQuote(asset.symbol, fh)]);
      else if (mv) await appStore.setQuotes(await massiveSnapshots([asset.symbol], mv));
      else await appStore.setQuotes((await tmxQuotes([{ key: quoteKey(asset), symbol: tmxSymbol({ symbol: asset.symbol, market: 'US' }), kind: 'equity' }])).quotes);
    } else if (asset.assetClass === 'fund') {
      await appStore.setQuotes((await tmxQuotes(tmxFundRequests([asset]))).quotes);
    } else if (asset.assetClass === 'cash') {
      await refreshFx(appStore.get().assets, true);
    }
  } catch (e) {
    appStore.markStale([quoteKey(asset)]);
    appStore.notify(t('notice.priceFailed', { symbol: asset.symbol, error: describeMarketError(e) }));
  }
}
